// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import type { CryptoEngine } from "./crypto";
import type { Homeserver } from "./api";
import type { Session } from "./types";
import { collectLocalKeyring } from "./keyring";
import { isLocked, unlockedKeyringPrivs } from "./keylock";
import { isBioLocked, unlockedBioPrivs } from "./biolock";

// keybackup.ts keeps the recovery key IN MEMORY for the lifetime of the session only —
// never persisted to disk — so newly-minted device keys can be silently re-backed-up
// (auto-refresh). It's cleared on logout, so the next login re-prompts to re-enable
// auto-backup. The server never sees the recovery key; a new device still must enter
// it to restore.
let sessionRecoveryKey: string | null = null;

export function setSessionRecoveryKey(key: string | null): void {
  sessionRecoveryKey = key && key.trim() ? key.trim() : null;
}
export function hasSessionRecoveryKey(): boolean {
  return sessionRecoveryKey !== null;
}
export function clearSessionRecoveryKey(): void {
  sessionRecoveryKey = null;
}

// currentKeyring returns this device's private keyring, honouring the at-rest tier
// (a locked/bio-locked keyring exposes its decrypted keys only in memory).
async function currentKeyring(userId: string): Promise<string[]> {
  if (isLocked(userId)) return unlockedKeyringPrivs();
  if (isBioLocked(userId)) return unlockedBioPrivs();
  return collectLocalKeyring(userId);
}

// autoBackup pushes a fresh encrypted backup of the current keyring under the held
// session recovery key. No-op (returns false) when no key is held or the keyring is
// empty. Called after the engine is ready (captures a just-minted device key).
export async function autoBackup(engine: CryptoEngine, hs: Homeserver, session: Session): Promise<boolean> {
  if (!sessionRecoveryKey) return false;
  const keyring = await currentKeyring(session.user_id);
  if (keyring.length === 0) return false;
  const { blob } = await engine.backupKeyring(sessionRecoveryKey, JSON.stringify(keyring));
  await hs.putKeyBackup(session.access_token, blob);
  return true;
}

// verifyAndHold checks a recovery key against the existing backup and, if it decrypts,
// retains it for the session (so the reminder flow can enable auto-backup safely).
export async function verifyAndHold(engine: CryptoEngine, hs: Homeserver, session: Session, key: string): Promise<boolean> {
  const blob = await hs.getKeyBackup(session.access_token);
  if (!blob) return false;
  try {
    await engine.restoreKeyring(key.trim(), blob);
    setSessionRecoveryKey(key);
    return true;
  } catch {
    return false;
  }
}
